import { getRuleVersionDocBundle } from '@/lib/posture-analysis/evaluation-rules-load';
import type { SopPhaseGoals } from '@/lib/posture-analysis/rule-params';

export type ProgressionPhaseCode = 'P1' | 'P2' | 'P3';

export interface PhaseGoalRow {
  id: string;
  label: string;
  goal: string;
  rationale: string;
  /** APT-only informational indicator (not enforced as a Phase 1 gate). */
  aptOnly?: boolean;
}

export interface PhaseGoalsBundle {
  p1Common: SopPhaseGoals;
  p2: SopPhaseGoals;
  p3: SopPhaseGoals;
}

function readGoals(docKey: string): SopPhaseGoals {
  const bundle = getRuleVersionDocBundle('current');
  const doc = bundle[docKey] as { sopPhaseGoals?: SopPhaseGoals } | undefined;
  return doc?.sopPhaseGoals ?? {};
}

/** Sync read of phase gates from evaluation-rules.yaml (single source of truth). */
export function getProgressionPhaseGoals(): PhaseGoalsBundle {
  return {
    p1Common: {
      ...readGoals('APT_P1'),
      ...readGoals('SB_P1'),
    },
    p2: readGoals('P2'),
    p3: readGoals('P3'),
  };
}

const GOAL_SPECS: Array<{
  id: string;
  label: string;
  goalKey: keyof SopPhaseGoals;
  rationale: string;
  format?: (value: number) => string;
}> = [
  {
    id: 'shoulder-ir',
    label: 'Shoulder IR',
    goalKey: 'shoulderIRMinDeg',
    rationale: 'Anterior rib-cage expansion and upper-body rotation.',
  },
  {
    id: 'slr',
    label: 'Straight Leg Raise',
    goalKey: 'slrMinDeg',
    rationale: 'Hip IR and posterior hip-capsule mobility.',
  },
  {
    id: 'hip-extension',
    label: 'Hip Extension',
    goalKey: 'hipExtensionMinDeg',
    rationale: 'Full extension before asymmetrical loading.',
    format: (v) => (v === 0 ? '0° (full)' : `≥${v}°`),
  },
  {
    id: 'shoulder-abd',
    label: 'Shoulder Abduction',
    goalKey: 'shoulderAbdMinDeg',
    rationale: 'Posterior upper expansion (T2–7).',
  },
  {
    id: 'pelvis-rotation',
    label: 'Pelvis Rotation',
    goalKey: 'pelvisRotationMinDeg',
    rationale: 'Sacrum and pelvis rotational capacity.',
  },
  {
    id: 'hip-abduction',
    label: 'Hip Abduction',
    goalKey: 'hipAbductionMinDeg',
    rationale: 'Hip ER and counternutation for stance.',
    format: (v) => `≈${v}°`,
  },
  {
    id: 'grounding',
    label: 'Grounding Performance',
    goalKey: 'groundingPerformanceMinScore',
    rationale: 'Force transfer — perceived ≥8/10 or dynamometer baseline gain.',
    format: (v) => `≥${v}/10 (or dynamometer)`,
  },
];

function formatGoalValue(value: number | undefined, format?: (value: number) => string): string {
  if (value === undefined) return '—';
  if (format) return format(value);
  return `≥${value}°`;
}

/** Build the bilateral assessment-gate rows that define when a phase is cleared. */
export function buildPhaseGoalRows(phase: ProgressionPhaseCode, goals: PhaseGoalsBundle): PhaseGoalRow[] {
  const source = phase === 'P1' ? goals.p1Common : phase === 'P2' ? goals.p2 : goals.p3;

  const rows: PhaseGoalRow[] = GOAL_SPECS.filter((spec) => source[spec.goalKey] !== undefined).map((spec) => ({
    id: spec.id,
    label: spec.label,
    goal: formatGoalValue(source[spec.goalKey], spec.format),
    rationale: spec.rationale,
  }));

  if (phase === 'P1') {
    rows.push({
      id: 'shoulder-abd-apt-info',
      label: 'Shoulder Abduction',
      goal: '≥25° (APT only)',
      rationale: 'Clinical indicator for anterior-tilt clients — not a hard gate.',
      aptOnly: true,
    });
  }

  return rows;
}
